import { useTranslation } from 'react-i18next';
import { isPreviewEmptyCode } from '../../../Models/DataSources/SmartSendWhatsApp';

// SmartSend part B — the ONE place a §4 `Message` becomes text.
//
// 🔴 WHY A HELPER AND NOT `t('…error.' + code)` AT EACH SITE. Every one of those sites renders a
// code that came off the WIRE, and the client does not get to assume the server's vocabulary is
// the one this build shipped. A code with no text — a new §4 row, a server ahead of the client,
// a typo in a `Message` — renders the KEY ITSELF: the operator is shown the literal string
// `DataSources.send.whatsapp.error.WA_SOMETHING_NEW` on a screen whose next button charges per
// message. `i18n.exists` is the gate, and it is the same gate `SendSummaryDialog` already uses
// on the 409 pre-gate headline; putting it in one function is what stops the six call sites from
// disagreeing about it.
//
// The fallback is `WA_INTERNAL` ("שגיאה פנימית בשליחת הוואטסאפ — פנו לתמיכה") — the one text in
// the set that is true of any unrecognised refusal and that tells the operator what to do.

export const WA_ERROR_PREFIX = 'DataSources.send.whatsapp.error.';
const FALLBACK = `${WA_ERROR_PREFIX}WA_INTERNAL`;

export interface WaErrorText {
    /** Human text for a §4 `Message`, never a raw key. */
    waErrorText: (code: string | null | undefined) => string;
    /** True when the code is one of v1.21's two 406 codes — an EMPTY STATE, not a failure. */
    isEmptyState: (code: string | null | undefined) => boolean;
}

export const useWaErrorText = (): WaErrorText => {
    const { t, i18n } = useTranslation();
    return {
        waErrorText: (code) => {
            // The prefix is written out rather than interpolated from `WA_ERROR_PREFIX`, and that
            // is load-bearing: `i18nParity.selftest` scans the SOURCE for
            // `DataSources.send.whatsapp.error.${` as the declared dynamic pattern that covers
            // every §4 code. Hiding it behind a constant would make all twenty-two look orphaned
            // and the direction-(c) gate would start reporting noise instead of defects.
            const key = `DataSources.send.whatsapp.error.${code}`;
            return code && i18n.exists(key) ? t(key) : t(FALLBACK);
        },
        // Re-exported through the same hook so a component never imports two modules to answer
        // one question about one code.
        isEmptyState: isPreviewEmptyCode,
    };
};

export default useWaErrorText;
